import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Layout from '../components/layoutUser';
import { formatRupiah } from '../utils/formatter';
import { FaBook, FaCalendarAlt, FaCheckCircle, FaExclamationTriangle, FaHistory, FaHourglassHalf, FaClock, FaTimes } from 'react-icons/fa';

const PeminjamanBuku = () => {
    const [activeTab, setActiveTab] = useState('active');
    const [peminjamanAktif, setPeminjamanAktif] = useState([]);
    const [riwayatPeminjaman, setRiwayatPeminjaman] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getLoggedInUserId = () => {
        const userString = localStorage.getItem('user');
        if (userString) {
            const user = JSON.parse(userString);
            return user.id;
        }
        return null;
    };
  
    const idAnggota = getLoggedInUserId();

    const fetchData = async () => {
        if (!idAnggota) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const [activeResponse, historyResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/peminjaman/user/${idAnggota}/active`),
                axios.get(`${process.env.REACT_APP_API_URL}/peminjaman/user/${idAnggota}/history`)
            ]);
            setPeminjamanAktif(activeResponse.data);
            setRiwayatPeminjaman(historyResponse.data);
        } catch (error) {
            Swal.fire('Gagal', 'Gagal memuat data peminjaman. Silakan coba lagi.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [idAnggota]);

    const handleAjukanPengembalian = async (id) => {
        Swal.fire({
            title: 'Konfirmasi Pengajuan',
            text: 'Apakah Anda yakin ingin mengajukan pengembalian buku ini?',
            icon: 'info',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Ya, Ajukan!',
            cancelButtonText: 'Batal'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.put(`${process.env.REACT_APP_API_URL}/peminjaman/ajukan-pengembalian/${id}`);
                    Swal.fire('Berhasil!', 'Pengajuan pengembalian berhasil! Menunggu verifikasi admin.', 'success');
                    fetchData();
                } catch (error) {
                    Swal.fire('Gagal', 'Gagal mengajukan pengembalian. Silakan coba lagi.', 'error');
                }
            }
        });
    };
    
    const PeminjamanCard = ({ item, type, index }) => {
        const getStatusInfo = () => {
            if (item.status_persetujuan === 'pending') {
                return {
                    icon: <FaClock className="status-pending" />,
                    text: 'Menunggu Persetujuan',
                    color: 'status-pending',
                    description: 'Pengajuan Anda sedang menunggu persetujuan admin (maks. 24 jam)'
                };
            }
            if (item.status_peminjaman === 'Dipinjam') {
                return {
                    icon: <FaHourglassHalf className="status-dipinjam" />,
                    text: 'Sedang Dipinjam',
                    color: 'status-dipinjam'
                };
            }
            if (item.status_peminjaman === 'Diajukan Pengembalian') {
                return {
                    icon: <FaCheckCircle className="status-verifikasi" />,
                    text: 'Menunggu Verifikasi',
                    color: 'status-verifikasi'
                };
            }
            return { icon: null, text: item.status_peminjaman, color: '' };
        };

        const statusInfo = getStatusInfo();

        return (
            <div className="peminjaman-card" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="card-header">
                    <FaBook className="book-icon" />
                    <h3 className="book-title">{item.judul_buku}</h3>
                </div>
                <div className="card-body">
                    {type === 'active' && item.status_persetujuan === 'pending' && (
                        <div className="notification is-warning is-light p-3 mb-3">
                            <p className="is-size-7 mb-1">
                                <strong>⏳ Status: Menunggu Persetujuan Admin</strong>
                            </p>
                            <p className="is-size-7">
                                Pengajuan Anda akan otomatis expired jika tidak disetujui dalam 24 jam.
                            </p>
                        </div>
                    )}
                    
                    <div className="info-row">
                        <FaCalendarAlt />
                        <span>Tgl. Pinjam:</span>
                        <strong>{new Date(item.tanggal_peminjaman).toLocaleDateString('id-ID')}</strong>
                    </div>
                    <div className="info-row">
                        <FaCalendarAlt />
                        <span>Tgl. Kembali:</span>
                        <strong>{new Date(item.tanggal_kembali || item.tanggal_pengembalian).toLocaleDateString('id-ID')}</strong>
                    </div>
                    {type === 'active' ? (
                        <div className="info-row">
                            {statusInfo.icon}
                            <span>Status:</span>
                            <span className={`status-tag ${statusInfo.color}`}>
                                {statusInfo.text}
                            </span>
                        </div>
                    ) : (
                        <>
                            {item.status_persetujuan === 'rejected' && item.keterangan_admin && (
                                <div className="notification is-danger is-light p-3 mb-3">
                                    <p className="is-size-7 mb-1">
                                        <strong><FaTimes /> Ditolak Admin</strong>
                                    </p>
                                    <p className="is-size-7">
                                        Alasan: {item.keterangan_admin}
                                    </p>
                                </div>
                            )}
                            {item.status_persetujuan === 'expired' && (
                                <div className="notification is-warning is-light p-3 mb-3">
                                    <p className="is-size-7">
                                        <strong>⏰ Expired</strong> - Pengajuan tidak disetujui dalam 24 jam
                                    </p>
                                </div>
                            )}
                            {item.denda !== undefined && (
                                <div className="info-row">
                                    <FaExclamationTriangle className="denda-icon" />
                                    <span>Denda:</span>
                                    <strong className={item.denda > 0 ? 'has-text-danger' : 'has-text-success'}>
                                        {formatRupiah(item.denda)}
                                    </strong>
                                </div>
                            )}
                        </>
                    )}
                </div>
                {type === 'active' && 
                 item.status_peminjaman === 'Dipinjam' && 
                 item.status_persetujuan === 'approved' && (
                    <div className="card-footer">
                        <button 
                            className="button is-success is-fullwidth"
                            onClick={() => handleAjukanPengembalian(item.id)}
                        >
                            Ajukan Pengembalian
                        </button>
                    </div>
                )}
                {type === 'active' && item.status_persetujuan === 'pending' && (
                    <div className="card-footer">
                        <button 
                            className="button is-warning is-fullwidth"
                            disabled
                        >
                            <FaClock className="mr-2" />
                            Menunggu Persetujuan
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderContent = (data, type) => {
        if (loading) {
            return <p className="has-text-centered">Memuat data...</p>;
        }
        if (data.length === 0) {
            return (
                <div className="no-data-placeholder">
                    {type === 'active' ? <FaBook size={40}/> : <FaHistory size={40}/>}
                    <p>
                        {type === 'active' 
                            ? "Tidak ada peminjaman aktif." 
                            : "Tidak ada riwayat peminjaman."}
                    </p>
                </div>
            );
        }
        return (
            <div className="peminjaman-grid">
                {data.map((item, index) => (
                    <PeminjamanCard key={item.id} item={item} type={type} index={index} />
                ))}
            </div>
        );
    };

    return (
        <Layout>
            <h2 className="title is-4">Halaman Peminjaman Buku</h2>
            
            <div className="notification is-info is-light mb-4">
                <p className="mb-2"><strong>📋 Informasi Peminjaman:</strong></p>
                <ul style={{ marginLeft: '1.5rem' }}>
                    <li>Setiap pengajuan peminjaman harus disetujui admin terlebih dahulu</li>
                    <li>Pengajuan akan otomatis expired jika tidak disetujui dalam 24 jam</li>
                    <li>Maksimal 3 buku per hari dapat dipinjam</li>
                    <li>Pastikan mengembalikan buku tepat waktu untuk menghindari denda</li>
                </ul>
            </div>

            <div className="tabs is-boxed">
                <ul>
                    <li className={activeTab === 'active' ? 'is-active' : ''}>
                        <a onClick={() => setActiveTab('active')}>
                            <span>Peminjaman Aktif</span>
                        </a>
                    </li>
                    <li className={activeTab === 'history' ? 'is-active' : ''}>
                        <a onClick={() => setActiveTab('history')}>
                            <span>Riwayat Peminjaman</span>
                        </a>
                    </li>
                </ul>
            </div>

            <div className="box content-box">
                {activeTab === 'active' 
                    ? renderContent(peminjamanAktif, 'active') 
                    : renderContent(riwayatPeminjaman, 'history')}
            </div>
            
            <style>{`
                .peminjaman-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(${isMobile ? '280px' : '320px'}, 1fr));
                    gap: 1.5rem;
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .peminjaman-card {
                    background: #fff;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                    display: flex;
                    flex-direction: column;
                    transition: all 0.3s ease;
                    opacity: 0;
                    animation: slideUp 0.5s ease-out forwards;
                }
                
                .peminjaman-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 8px 16px rgba(0,0,0,0.12);
                }

                .card-header {
                    padding: 1rem;
                    border-bottom: 1px solid #eee;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }
                
                .book-icon {
                    color: #3273dc;
                }

                .book-title {
                    margin: 0;
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #363636;
                }

                .card-body {
                    padding: 1rem;
                    flex-grow: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }
                
                .info-row {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.9rem;
                    color: #7a7a7a;
                }
                
                .info-row strong {
                    color: #363636;
                    margin-left: auto;
                }

                .status-tag {
                    font-weight: bold;
                    padding: 0.2rem 0.6rem;
                    border-radius: 12px;
                    font-size: 0.8rem;
                    color: #fff;
                }

                .status-pending { 
                    background-color: #ffdd57; 
                    color: #947600 !important; 
                }
                .status-dipinjam { 
                    color: #ff9f43; 
                }
                .status-verifikasi { 
                    background-color: #48c78e;
                    color: #fff !important;
                }
                
                .denda-icon { color: #f14668; }

                .card-footer {
                    padding: 1rem;
                    background: #fafafa;
                    border-top: 1px solid #eee;
                }
                
                .no-data-placeholder {
                    text-align: center;
                    padding: 3rem 1rem;
                    color: #7a7a7a;
                    opacity: 0.7;
                }
                
                .no-data-placeholder p {
                    margin-top: 1rem;
                    font-size: 1.1rem;
                }
                
                .tabs.is-boxed a {
                    font-weight: 500;
                }

                .box.content-box {
                    min-height: 300px;
                }
            `}</style>
        </Layout>
    );
};

export default PeminjamanBuku;